import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";
import Logo from "../components/Logo";
import usePageTitle from "../hooks/usePageTitle";
import { ChevronRight, Star, MapPin, Percent, CreditCard, QrCode, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  usePageTitle(null, 'Advanta Plus - Premium travel membership for Costa del Sol. Get exclusive discounts at restaurants, shops and services.');
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState({});
  const [territory, setTerritory] = useState(localStorage.getItem("advanta_territory") || "costa_del_sol");
  const affiliateRef = searchParams.get("ref");

  const [showAffiliateBanner, setShowAffiliateBanner] = useState(false);

  const territories = {
    costa_del_sol: {
      name: "Costa del Sol",
      image: "https://images.unsplash.com/photo-1613429561582-2b20fc0028dd?w=1920",
      tagline: "Marbella, Estepona, Fuengirola & more",
    },
    tenerife: {
      name: "Tenerife",
      image: "https://images.unsplash.com/photo-1613333046289-4cd8e6a6270e?w=1920&q=80",
      tagline: "Los Cristianos, Costa Adeje, Playa de las Americas & more",
    },
    majorca: {
      name: "Mallorca",
      image: "https://images.unsplash.com/photo-1559202987-59d8a2827511?w=1920&q=80",
      tagline: "Palma, Magaluf, Alcudia, Pollensa & more",
    },
  };

  const switchTerritory = (tid) => {
    setTerritory(tid);
    localStorage.setItem("advanta_territory", tid);
  };

  useEffect(() => {
    // Store affiliate code if present
    if (affiliateRef) {
      localStorage.setItem("advanta_affiliate", affiliateRef);
      setShowAffiliateBanner(true);
    }
    fetchPlans();
  }, [affiliateRef]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/membership/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  };

  const handleGetStarted = () => {
    navigate("/membership");
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#020617]/95 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between">
          <Logo size="large" />
          <div className="hidden md:flex items-center gap-8">
            <Link to="/businesses" className="text-white/80 hover:text-[#D4AF37] transition-colors link-underline">{t('nav.businesses')}</Link>
            <a href="#pricing" className="text-white/80 hover:text-[#D4AF37] transition-colors link-underline">{t('landing.pricing')}</a>
            <a href="#features" className="text-white/80 hover:text-[#D4AF37] transition-colors link-underline">{t('landing.features')}</a>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            <LanguageSelector />
            {user ? (
              <Link to="/dashboard">
                <Button className="btn-gold px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded text-xs sm:text-sm md:text-base" data-testid="dashboard-btn">{t('nav.dashboard')}</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-2 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm md:text-base" data-testid="login-btn">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-gold px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded text-xs sm:text-sm md:text-base" data-testid="register-btn">
                    {t('nav.register')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${territories[territory].image}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-[#020617]/60 to-[#020617]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-44 pb-20 text-center">
          {/* Territory Tabs */}
          <div className="flex items-center justify-center gap-2 mb-8 animate-fade-in-up">
            {Object.entries(territories).map(([tid, t_data]) => (
              <button
                key={tid}
                onClick={() => switchTerritory(tid)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all ${
                  territory === tid
                    ? 'bg-[#D4AF37] text-[#020617] shadow-lg shadow-[#D4AF37]/20'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                {t_data.name}
              </button>
            ))}
          </div>

          <div className="animate-fade-in-up">
            <span className="inline-block px-4 py-2 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium mb-6 tracking-widest uppercase">
              {t('landing.exclusive_membership')}
            </span>
          </div>

          <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl text-white mb-6 animate-fade-in-up animation-delay-100">
            {t('landing.hero_title')} <span className="text-gold-gradient italic">{territories[territory].name}</span>
            <span className="sr-only"> — Discount Card & App for Costa del Sol and Tenerife</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/70 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200 font-light">
            {t('landing.hero_description')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
            <Button 
              onClick={handleGetStarted}
              className="btn-gold px-10 py-6 text-lg rounded group"
              data-testid="hero-get-started-btn"
            >
              {t('landing.get_started')}
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Link to="/businesses">
              <Button variant="ghost" className="btn-gold-outline px-10 py-6 text-lg rounded" data-testid="explore-btn">
                {t('landing.explore_partners')}
              </Button>
            </Link>
          </div>
          
          <div className="mt-16 flex items-center justify-center gap-8 text-white/60 animate-fade-in-up animation-delay-400">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              <span>{t('landing.partners_count')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-[#D4AF37]" />
              <span>{t('landing.discount_amount')}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
              <span>{t('landing.coast_wide')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-[#020617] relative noise-overlay">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-widest uppercase">{t('landing.how_it_works')}</span>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mt-4">
              {t('landing.your_luxury')} <span className="italic text-[#D4AF37]">{t('landing.simplified')}</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: CreditCard, title: t('landing.step1_title'), desc: t('landing.step1_desc') },
              { icon: QrCode, title: t('landing.step2_title_short'), desc: t('landing.step2_desc') },
              { icon: Percent, title: t('landing.step3_title_short'), desc: t('landing.step3_desc') },
            ].map((feature, index) => (
              <div 
                key={index}
                className="p-8 rounded-2xl border border-[#D4AF37]/20 bg-[#0B0F19] hover:border-[#D4AF37]/50 transition-all duration-500 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-[#0B0F19] relative noise-overlay">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-widest uppercase">{t('landing.membership_options')}</span>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mt-4">
              {t('landing.choose_your')} <span className="italic text-[#D4AF37]">{t('landing.adventure')}</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(plans).map(([id, plan], index) => (
              <div 
                key={id}
                className={`pricing-card rounded-2xl p-8 text-center ${index === 3 ? 'featured' : ''}`}
                data-testid={`pricing-card-${id}`}
              >
                {index === 3 && (
                  <span className="inline-block px-4 py-1 rounded-full bg-[#D4AF37] text-[#020617] text-xs font-bold tracking-wider uppercase mb-4">
                    {t('landing.best_value')}
                  </span>
                )}
                <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">{plan.name}</h3>
                <p className="text-white/50 text-sm mb-6">{plan.duration_days} {t('landing.days_access')}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#D4AF37]">€{plan.price}</span>
                </div>
                <ul className="text-white/60 text-sm space-y-3 mb-8">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                    {t('landing.digital_card')}
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                    {t('landing.discounts_range')}
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                    {t('landing.all_partners')}
                  </li>
                </ul>
                <Button 
                  onClick={handleGetStarted}
                  className={index === 3 ? "btn-gold w-full py-4 rounded" : "btn-gold-outline w-full py-4 rounded"}
                  data-testid={`select-plan-${id}`}
                >
                  {t('landing.select_plan')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Preview */}
      <section className="py-24 bg-[#F8F8F6]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-widest uppercase">{t('landing.our_partners')}</span>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#020617] mt-4">
              {t('landing.curated')} <span className="italic text-[#D4AF37]">{t('landing.experiences')}</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { category: t('directory.dining'), count: "20+", icon: "🍽️", desc: t('landing.dining_desc') },
              { category: t('directory.shopping'), count: "15+", icon: "🛍️", desc: t('landing.shopping_desc') },
              { category: t('directory.services'), count: "15+", icon: "✨", desc: t('landing.services_desc') },
            ].map((cat, index) => (
              <Link 
                to={`/businesses?category=${cat.category.toLowerCase()}`}
                key={index}
                className="group p-8 rounded-2xl bg-white border border-gray-100 hover:border-[#D4AF37] hover:shadow-luxury transition-all duration-500"
              >
                <span className="text-4xl mb-4 block">{cat.icon}</span>
                <h3 className="font-['Playfair_Display'] text-2xl text-[#020617] mb-2">{cat.category}</h3>
                <p className="text-[#D4AF37] font-bold mb-2">{cat.count} {t('landing.partners')}</p>
                <p className="text-gray-500">{cat.desc}</p>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/businesses">
              <Button className="btn-gold-outline px-10 py-4 rounded border-[#020617] text-[#020617] hover:bg-[#020617] hover:text-white" data-testid="view-all-partners-btn">
                {t('landing.view_all')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#020617] relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1654363539422-76c4e6b93b3d?w=1920')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white mb-6">
            {t('landing.start_saving')} <span className="italic text-[#D4AF37]">{t('landing.today')}</span>
          </h2>
          <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
            {t('landing.join_members')}
          </p>
          <Button 
            onClick={handleGetStarted}
            className="btn-gold px-12 py-6 text-lg rounded"
            data-testid="cta-join-btn"
          >
            {t('landing.get_membership')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#020617] border-t border-[#D4AF37]/10 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 relative">
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-[#D4AF37] via-[#F5DEB3] to-[#B8860B]" />
                  <div className="absolute inset-[2px] rounded-[6px] bg-[#020617]" />
                  <div className="relative h-full flex items-center justify-center">
                    <span 
                      className="font-bold text-sm"
                      style={{
                        background: "linear-gradient(135deg, #D4AF37 0%, #F5DEB3 50%, #D4AF37 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >A+</span>
                  </div>
                </div>
                <span className="font-['Playfair_Display'] text-xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
              </div>
              <p className="text-white/40 text-sm">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer.for_members')}</h4>
              <div className="space-y-2 text-white/50 text-sm">
                <Link to="/businesses" className="block hover:text-[#D4AF37] transition-colors">{t('footer.directory')}</Link>
                <a href="#pricing" className="block hover:text-[#D4AF37] transition-colors">{t('landing.pricing')}</a>
                <Link to="/login" className="block hover:text-[#D4AF37] transition-colors">{t('nav.login')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer.for_business')}</h4>
              <div className="space-y-2 text-white/50 text-sm">
                <Link to="/partner-signup" className="block hover:text-[#D4AF37] transition-colors">{t('footer.become_partner')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer.affiliates')}</h4>
              <div className="space-y-2 text-white/50 text-sm">
                <Link to="/affiliate-signup" className="block hover:text-[#D4AF37] transition-colors">{t('footer.become_affiliate')}</Link>
                <span className="block text-[#D4AF37]">{t('footer.earn_commission')}</span>
              </div>
            </div>
          </div>
          {/* SEO content block */}
          <div className="border-t border-white/10 pt-8 mb-8">
            <p className="text-white/25 text-xs leading-relaxed max-w-4xl mx-auto text-center">
              Advanta Plus is the leading discount card and app for Costa del Sol, Tenerife and Mallorca.
              Save at restaurants, bars, shops, spas and experiences in Marbella, Malaga, Puerto Banus,
              Estepona, Fuengirola, Benalmadena, Torremolinos, Palma, Magaluf, Alcudia, Pollensa,
              Los Cristianos, Costa Adeje and Playa de las Americas. Our digital discount card works
              instantly on your phone — no physical card needed. Whether you're on holiday or living
              in Spain, get exclusive deals and discounts across Spain's most popular destinations.
              Available as a weekly, fortnightly or monthly pass.
            </p>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/40 text-sm">
            © 2026 Advanta Plus. {t('footer.rights')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
